import type ExcelJSType from "exceljs";
import type { ColumnDef } from "../columns";
import { resolveAccessor } from "../columns";
import { type FormatterBundle, formatCell } from "../lib/format";

// `exceljs`'s Node entry (`main: "./excel.js"`) pulls in `unzipper` which
// pulls in `fstream` which `require`s the deprecated `rimraf` package.
// Under Next 15 webpack happened to silently tree-shake the chain;
// Turbopack in Next 16 walks it eagerly during the SSR pass for any
// Client Component that imports this module, and the build fails with
// `Module not found: Can't resolve 'rimraf'`. Loading exceljs lazily at
// call-time (a) avoids the SSR walk entirely — the chunk is browser-only —
// and (b) defers the ~1 MB exceljs download until the user actually
// triggers an XLSX export. The dynamic import is await-ed once and the
// resolved namespace is cached so repeated exports don't re-fetch.
let excelJsPromise: Promise<typeof ExcelJSType> | null = null;
async function loadExcelJs(): Promise<typeof ExcelJSType> {
  if (!excelJsPromise) {
    excelJsPromise = import("exceljs").then(
      (m) => (m.default ?? m) as typeof ExcelJSType,
    );
  }
  return excelJsPromise;
}

/**
 * MIME type used for XLSX blobs. Matches the OOXML spec used by Excel,
 * LibreOffice Calc, Google Sheets, and Numbers.
 */
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Default sheet width, in characters, used when a column does not declare
 * an explicit `width` (in px) and {@link toXlsx} can't derive one. exceljs
 * sizes columns in characters at the default font; ~12 chars maps to ~100px
 * which is the same default the table engine uses for unsized columns.
 */
const DEFAULT_COL_WIDTH_CHARS = 12;

/** Approximate px-to-character conversion at the default font (Calibri 11pt). */
const PX_PER_CHAR = 8;

/**
 * Map a {@link ColumnDef} to an exceljs `numFmt` string when the column type
 * has a built-in format. Returning `undefined` lets exceljs use the General
 * format, which is right for `text` and `custom` columns where values are
 * already formatted strings.
 *
 * - money → "#,##0.00 [Currency]" (Excel-localised symbol via accounting fmt).
 * - percent → "0.00%".
 * - date → "yyyy-mm-dd".
 * - datetime → "yyyy-mm-dd hh:mm".
 * - number → "#,##0.##".
 *
 * Values are written as raw numeric / Date objects (not pre-formatted strings)
 * for these types so that Excel's own column-format takes effect. Callers
 * filtering or pivot-tabling the export rely on the cells being typed.
 */
function numFmtForType<TRow>(column: ColumnDef<TRow>): string | undefined {
  switch (column.type) {
    case "money": {
      // Use the column's currency symbol where available; Excel's accounting
      // format slot is locale-sensitive so we just use the ISO code as a
      // suffix. This matches what most users expect when re-opening the file.
      const currency = column.currency ?? "EUR";
      return `#,##0.00 "${currency}"`;
    }
    case "percent":
      return "0.00%";
    case "date":
      return "yyyy-mm-dd";
    case "datetime":
      return "yyyy-mm-dd hh:mm";
    case "number":
      return "#,##0.##";
    default:
      return undefined;
  }
}

/**
 * Decide the cell value to write for a given column type. Returning a number
 * or Date keeps the cell typed (so Excel sums columns); strings are used for
 * text/enum/relation/custom where format-as-text is the right behaviour.
 *
 * For `null` / `undefined` we return `null` so exceljs leaves the cell empty
 * — writing "" would generate a string-typed empty cell, which makes filters
 * misbehave on numeric columns.
 */
function cellValueForType<TRow>(
  value: unknown,
  column: ColumnDef<TRow>,
  locale: string,
  formatters: FormatterBundle,
  row: TRow,
): number | string | Date | null {
  if (value === null || value === undefined) return null;

  switch (column.type) {
    case "number":
    case "money":
    case "percent":
      // Numeric types: write as raw number so Excel's column format renders
      // the locale-correct currency / percent / grouping. We still validate
      // via formatCell — if the column has a custom `format`, callers expect
      // the formatted string in the export (their override wins).
      if (column.format) {
        return formatCell(value, column, locale, formatters, { row });
      }
      if (typeof value !== "number") {
        throw new Error(
          `Column "${column.key}" of type "${column.type}" received non-number value`,
        );
      }
      return value;

    case "date":
    case "datetime":
      if (column.format) {
        return formatCell(value, column, locale, formatters, { row });
      }
      if (!(value instanceof Date)) {
        throw new Error(
          `Column "${column.key}" of type "${column.type}" received non-Date value`,
        );
      }
      return value;

    case "boolean":
      // exceljs supports boolean cells but most users want "Yes"/"No" so
      // they can sort/filter as text. Mirror what formatCell returns.
      return formatCell(value, column, locale, formatters, { row });

    case "text":
    case "enum":
    case "relation":
    case "custom":
      // Always pass through formatCell so caller-provided enum labels / custom
      // formatters are honoured, and so the on-disk representation matches the
      // table.
      return formatCell(value, column, locale, formatters, { row });
  }
}

/**
 * Options accepted by {@link toXlsx}.
 */
export interface ToXlsxOptions<TRow> {
  /** Row data in display order (sort/filter already applied by caller). */
  rows: TRow[];
  /** Column definitions. Hidden columns must be omitted by the caller. */
  columns: ColumnDef<TRow>[];
  /** Locale tag passed to formatters (e.g. "sr-Latn", "en-GB"). */
  locale: string;
  /** Formatter bundle resolved from `@aio/platform-i18n` or test mocks. */
  formatters: FormatterBundle;
  /**
   * Sheet name. Excel limits this to 31 characters and forbids `:\/?*[]`;
   * callers are responsible for sanitising — we don't rewrite the input
   * silently because that breaks user expectations for filenames.
   */
  sheetName?: string;
}

/**
 * Serialize rows to an XLSX blob using exceljs.
 *
 * Highlights:
 * - Column widths are derived from `column.width` (px → exceljs character
 *   units) so the exported sheet visually matches the on-screen table.
 * - Numeric / date columns are typed (not pre-formatted strings) and styled
 *   via Excel's own number format, so users can re-format / sum / pivot
 *   without losing precision.
 * - Header row is styled bold with a subtle bg fill so it stands out when
 *   re-opened.
 *
 * The `Promise<Blob>` return type reflects exceljs's async writer surface.
 * In jsdom we use `workbook.xlsx.writeBuffer()` which works in browser-like
 * environments without touching the filesystem.
 */
export async function toXlsx<TRow>(options: ToXlsxOptions<TRow>): Promise<Blob> {
  const {
    rows,
    columns,
    locale,
    formatters,
    sheetName = "Sheet1",
  } = options;

  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "AIO";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(sheetName);

  // Configure columns — exceljs expects an array of {header, key, width, style}.
  sheet.columns = columns.map((column) => {
    // Derive width: prefer explicit px → chars conversion, fall back to default.
    const widthPx = column.width;
    const widthChars =
      typeof widthPx === "number" && widthPx > 0
        ? Math.max(4, Math.round(widthPx / PX_PER_CHAR))
        : DEFAULT_COL_WIDTH_CHARS;

    const numFmt = numFmtForType(column);

    return {
      header: column.header,
      key: column.key,
      width: widthChars,
      // exceljs lets us declare the column-level numFmt once; rows then inherit.
      // Wrapping in `style` rather than top-level so it survives header style.
      ...(numFmt
        ? { style: { numFmt } }
        : {}),
    };
  });

  // Style the header row — bold + subtle bg + outlined bottom edge.
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF1F5F9" }, // slate-100; matches platform muted bg
  };
  headerRow.border = {
    bottom: { style: "thin", color: { argb: "FFCBD5E1" } }, // slate-300
  };
  headerRow.alignment = { vertical: "middle" };
  headerRow.height = 22;

  // Add data rows.
  for (const row of rows) {
    const rowValues: Record<string, number | string | Date | null> = {};
    for (const column of columns) {
      const accessor = resolveAccessor(column);
      const value = accessor(row);
      rowValues[column.key] = cellValueForType(
        value,
        column,
        locale,
        formatters,
        row,
      );
    }
    sheet.addRow(rowValues);
  }

  // Right-align numeric columns at the column level so all data cells inherit.
  columns.forEach((column, idx) => {
    const colIndex = idx + 1; // 1-based
    if (
      column.type === "number" ||
      column.type === "money" ||
      column.type === "percent"
    ) {
      sheet.getColumn(colIndex).alignment = { horizontal: "right" };
    } else if (column.align) {
      sheet.getColumn(colIndex).alignment = { horizontal: column.align };
    }
  });

  // Freeze the header row so it stays visible while scrolling.
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  // exceljs returns an `ArrayBuffer | Buffer`; Blob accepts either. Casting
  // narrows to the BlobPart slice the constructor accepts in browser typings.
  return new Blob([buffer as ArrayBuffer], { type: XLSX_MIME_TYPE });
}
